<script>
    import { BarLoader } from 'svelte-loading-spinners';
    import { currentUser } from '../store/store.js'; // Only currentUser needed directly
    import { handleAnalyzeBrf, handleCreateBrfExpert, analyzeUploadedStadgar, analyzeUploadedEnergy } from '../utils/analyze.js'; // Added analyzeUploadedEnergy
    import { fetchBrf } from '../utils/utils.js';
    import { v4 as uuidv4 } from 'uuid';
    import { createEventDispatcher } from 'svelte';

    const dispatch = createEventDispatcher();

    export let uploadType = 'årsredovisning'; // 'årsredovisning', 'stadgar', etc.
    export let associatedCoreId = null; // For uploads linked to an existing entity
    export let textsConfig = {}; // Allow parent to override texts

    let fileInput = null;

    // Internal UI states for the component's own progress display
    let step1Loading = false; // Uploading file
    let step1Done = false;
    let step2Loading = false; // Analyzing
    let step2Done = false;
    let step3Loading = false; // Creating expert (initiation)
    let step3Done = false; // This will be true when expert creation is initiated. Actual completion is via global stores.

    let displayTexts;

    $: {
        let defaults;
        if (uploadType === 'stadgar') {
            defaults = {
                title: 'Ladda upp Stadgar',
                instruction: 'Klicka på knappen nedan för att välja stadgar (PDF) från din enhet.',
                step1Text: 'Laddar upp stadgar',
                step2Text: 'Analyserar stadgar',
                step3Text: 'Skapar expert på stadgar',
                buttonText: 'Välj fil (Stadgar)',
                infoText: 'Processen kan ta någon minut. Ha tålamod.'
            };
        } else if (uploadType === 'energy') {
            defaults = {
                title: 'Ladda upp Energideklaration',
                instruction: 'Klicka på knappen nedan för att välja energideklaration (PDF) från din enhet.',
                step1Text: 'Laddar upp energideklaration',
                step2Text: 'Analyserar energideklaration',
                step3Text: 'Slutför bearbetning', // Step 3 label for energy
                buttonText: 'Välj fil (Energideklaration)',
                infoText: 'Analys kan ta ca 20-30 sekunder.'
            };
        } else { // Default to årsredovisning
            defaults = {
                title: 'Ladda upp Årsredovisning',
                instruction: 'Klicka på knappen nedan för att välja en PDF-fil från din enhet.',
                step1Text: 'Laddar upp fil',
                step2Text: 'Analyserar förening',
                step3Text: 'Skapar expert på förening',
                buttonText: 'Välj fil',
                infoText: 'Analys kan i vanliga fall ta upp mot 30 sekunder.'
            };
        }
        displayTexts = { ...defaults, ...textsConfig };
    }
    
    function resetStates() {
        step1Loading = false; step1Done = false;
        step2Loading = false; step2Done = false;
        step3Loading = false; step3Done = false;
        if (fileInput) fileInput.value = ''; // Reset file input
    }

    function triggerFileInput() {
        resetStates(); // Reset states when triggering a new upload
        fileInput.click();
    }

    async function handleFileUpload(event) {
        const file = event.target.files[0];
        if (!file) return;

        step1Loading = true; step1Done = false;
        dispatch('progress', { stage: 'upload', type: uploadType, loading: true, done: false });

        try {
            validateFile(file);
            const fileNameInBucket = await uploadFileToBucket(file);
            
            step1Loading = false; step1Done = true;
            dispatch('progress', { stage: 'upload', type: uploadType, loading: false, done: true, fileName: fileNameInBucket });

            await handlePostUploadActions(fileNameInBucket);

        } catch (error) {
            console.error(`Error in ${uploadType} file upload process:`, error.message);
            dispatch('error', { type: uploadType, message: error.message });
            step1Loading = false; step1Done = false; // Reset this step on error
             // Ensure other steps are also reset visually if they were triggered
            step2Loading = false; step2Done = false;
            step3Loading = false; step3Done = false;
            dispatch('progress', { stage: 'upload', type: uploadType, loading: false, done: false, error: true });
        }
    }

    async function uploadFileToBucket(file) {
        const bucketName = 'brf-redovisningar-to-analyze';
        const uniqueFileNameBase = uuidv4();
        let fileNamePrefix = '';
        if (uploadType === 'stadgar') {
            fileNamePrefix = 'stadgar_';
        } else if (uploadType === 'energy') {
            fileNamePrefix = 'energy_'; // Prefix for energy files
        }
        else {
            fileNamePrefix = 'redovisning_';
        }
        const fileName = `${fileNamePrefix}${uniqueFileNameBase}`;
        const uploadUrl = `https://storage.googleapis.com/upload/storage/v1/b/${bucketName}/o?uploadType=media&name=${encodeURIComponent(fileName)}`;

        const response = await fetch(uploadUrl, {
            method: 'POST',
            headers: { 'Content-Type': file.type },
            body: file
        });

        if (!response.ok) throw new Error('Failed to upload file to bucket');
        
        console.log(`${uploadType} file uploaded successfully to bucket as:`, fileName);
        return fileName;
    }

    async function handlePostUploadActions(fileNameInBucket) {
        step2Loading = true; step2Done = false;
        dispatch('progress', { stage: 'analysis', type: uploadType, loading: true, done: false });
        
        step3Loading = true; step3Done = false;
        dispatch('progress', { stage: 'expert', type: uploadType, loading: true, done: false });

        try {
            let current_core_id = associatedCoreId;

            if (uploadType === 'årsredovisning') {
                // For årsredovisning, a new core_id might be created if not associated
                const newCoreId = await handleAnalyzeBrf(fileNameInBucket, current_core_id); // Allow passing existing core_id
                if (newCoreId) current_core_id = newCoreId; // Update if a new one was created
                step2Loading = false; step2Done = true;
                dispatch('progress', { stage: 'analysis', type: uploadType, loading: false, done: true });
                
                await handleCreateBrfExpert(fileNameInBucket, current_core_id);
                step3Loading = false; step3Done = true;
                dispatch('progress', { stage: 'expert', type: uploadType, loading: false, done: true });

            } else if (uploadType === 'stadgar') {
                if (!current_core_id) throw new Error('Associated Core ID is required for stadgar processing.');
                await analyzeUploadedStadgar(fileNameInBucket, current_core_id);
                step2Loading = false; step2Done = true;
                dispatch('progress', { stage: 'analysis', type: uploadType, loading: false, done: true });

                await handleCreateBrfExpert(fileNameInBucket, current_core_id);
                step3Loading = false; step3Done = true;
                dispatch('progress', { stage: 'expert', type: uploadType, loading: false, done: true });

            } else if (uploadType === 'energy') {
                if (!current_core_id) throw new Error('Associated Core ID is required for energy declaration processing.');
                await analyzeUploadedEnergy(fileNameInBucket, current_core_id);
                step2Loading = false; step2Done = true;
                dispatch('progress', { stage: 'analysis', type: uploadType, loading: false, done: true });

                step3Loading = false; step3Done = true;
                dispatch('progress', { stage: 'expert', type: uploadType, loading: false, done: true });
            }
            
            dispatch('complete', { type: uploadType, fileName: fileNameInBucket, coreId: current_core_id });

        } catch (error) {
            console.error(`Error in post-upload actions for ${uploadType}:`, error.message);
            dispatch('error', { type: uploadType, message: error.message, stage: step2Loading ? 'analysis' : 'unknown' });
            step2Loading = false; step2Done = false;
            step3Loading = false; step3Done = false; // Reset expert step if analysis failed before it
            dispatch('progress', { stage: 'analysis', type: uploadType, loading: false, done: false, error: true });
            dispatch('progress', { stage: 'expert', type: uploadType, loading: false, done: false, error: true });
        }
    }

    function validateFile(file) {
        if (!file || file.name.split('.').pop().toLowerCase() !== 'pdf') {
            throw new Error(`Uppladdad fil är ingen PDF. Filnamnet är: ${file?.name || 'okänt'}`);
        }
    }
</script>

{#if ($currentUser?.numberOfAnalysis < 10 || $currentUser?.type == "admin") || ((uploadType === 'stadgar' || uploadType === 'energy') && $currentUser) } <!-- Allow stadgar and energy upload if logged in -->
<div class="container upload-container">
    <div class="key-value-table solo-table">
        {#if step1Loading || step2Loading || step3Loading || step1Done } <!-- Show table if any step is active or upload is done -->
        <table class="upload-table">
            <tbody>
                <tr>
                    <td>{displayTexts.step1Text}</td>
                    <td class="center-content">
                        {#if step1Done}&#9989;{:else if step1Loading}<BarLoader size="40" color="#FF3E00" unit="px" duration="1s"/>{/if}
                    </td>
                </tr>
                <tr>
                    <td>{displayTexts.step2Text}</td>
                    <td class="center-content">
                        {#if step2Done}&#9989;{:else if step2Loading}<BarLoader size="40" color="#FF3E00" unit="px" duration="1s"/>{/if}
                    </td>
                </tr>
                <tr>
                    <td>{displayTexts.step3Text}</td>
                    <td class="center-content">
                        <!-- For expert, 'done' means initiated. Actual completion tracked by parent via global stores -->
                        {#if step3Done && !step3Loading}&#9203; <!-- Hourglass for initiated --> {:else if step3Loading}<BarLoader size="40" color="#FF3E00" unit="px" duration="1s"/>{/if}
                    </td>
                </tr>
            </tbody>
        </table>
        <div class="info-text">{displayTexts.infoText}</div>
        <!-- Option to upload another file after completion/error -->
        {#if (step1Done && !step1Loading && !step2Loading && !step3Loading) || (!step1Loading && !step2Loading && !step3Loading && !step1Done && !fileInput?.files?.length)}
          <p></p> <!-- spacing -->
           <span
            class="action-button upload-trigger"
            on:click={triggerFileInput}
            on:keydown={(e) => e.key === 'Enter' && triggerFileInput()}
            tabindex="0"
            role="button"
            aria-label="Ladda upp en till fil"
           >
            <i class="fa-solid fa-upload icon-prefix"></i> {displayTexts.buttonText.replace("Välj", "Ladda upp ny")}
           </span>
        {/if}
        {:else}
        <h3>{displayTexts.title}</h3>
        <p class="upload-instructions">{displayTexts.instruction}</p>
        <input type="file" bind:this={fileInput} on:change={handleFileUpload} accept=".pdf" class="styled-file-input" />
        <span
            class="action-button upload-trigger"
            on:click={triggerFileInput}
            on:keydown={(e) => e.key === 'Enter' && triggerFileInput()}
            tabindex="0"
            role="button"
            aria-label={displayTexts.buttonText}
        >
            <i class="fa-solid fa-upload icon-prefix"></i> {displayTexts.buttonText}
        </span>
        <div class="info-text">
            {#if uploadType === 'årsredovisning'}
            Kontrollera så att din BRF inte redan finns i systemet. Om du har problem, kontakta <a href="mailto:info@boanalys.io">info@boanalys.io</a>
            {:else if uploadType === 'stadgar'}
            Se till att du laddar upp föreningens stadgar i PDF-format.
            {:else if uploadType === 'energy'}
            Se till att du laddar upp föreningens energideklaration i PDF-format.
            {/if}
        </div>
        {/if}
    </div>
</div>
{/if}

<style>
	.upload-container {
		text-align: center;
	}

    .upload-table {
        width: 100%;
        margin-bottom: 15px;
        border-collapse: collapse;
    }
    .upload-table td {
        padding: 8px 5px;
        font-size: 0.9rem;
        color: #495057;
    }
    .upload-table td:first-child {
        text-align: left;
        padding-left: 0;
    }
     .upload-table td:last-child {
         text-align: right;
         padding-right: 0;
    }
     .upload-table .center-content {
        text-align: center;
    }
     .upload-table tr:not(:last-child) td {
        border-bottom: 1px dashed #eee;
    }

	.styled-file-input {
		display: none;
	}
    
    p.upload-instructions { /* Added from AnalyzeStadgar for consistency */
        font-size: 0.95rem;
        color: #495057;
        line-height: 1.6;
        margin-bottom: 20px;
        text-align: center;
    }

	.action-button.upload-trigger {
		background-color: #343a40;
		color: white;
		padding: 12px 25px;
		border: none;
		border-radius: 8px;
		cursor: pointer;
		font-weight: 600;
		transition: background-color 0.2s, transform 0.1s ease, box-shadow 0.2s ease;
		display: inline-block; /* Changed from block to inline-block */
		width: fit-content;
		text-align: center;
		font-size: 1rem;
		box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
		/* margin-left: auto; // Removed for inline-block centering via parent
		margin-right: auto; // Removed */
        margin-top: 10px; /* Added from AnalyzeStadgar */
        margin-bottom: 15px; /* Added from AnalyzeStadgar */
	}
	.action-button.upload-trigger:hover {
		background-color: #495057;
		transform: translateY(-2px);
		box-shadow: 0 5px 10px rgba(0, 0, 0, 0.15);
	}
	.action-button.upload-trigger:active {
		transform: translateY(0px);
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	}
    .action-button.upload-trigger .icon-prefix {
        margin-right: 8px;
    }

	.info-text {
		margin-top: 10px;
		font-size: 0.85rem;
		color: #6c757d;
		line-height: 1.5;
	}
	.info-text a {
		color: #4a6491;
		text-decoration: none;
	}
	.info-text a:hover {
		text-decoration: underline;
	}
</style>